import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Cpu, Info, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { adminPlatformSettingService } from "@/services/admin-platform-setting.service";
import {
  DEFAULT_VM_CPU_LIMIT_RATIO,
  parseVmCpuLimitRatio,
} from "@/lib/vmCpuLimit";
import type { PlatformSettingListDto } from "@/types/platform-setting";

const SETTING_KEY = "vm_cpu_limit_ratio";

/**
 * The row the backend reads: the most recently changed ACTIVE global row. With no active one, the
 * newest inactive global row is returned so saving switches it back on instead of adding a second.
 */
function pickGlobalRow(rows: PlatformSettingListDto[]) {
  const global = rows
    .filter((row) => !row.tenantId && !row.region)
    .sort((a, b) =>
      (b.modifiedAt ?? b.createdAt).localeCompare(a.modifiedAt ?? a.createdAt),
    );
  return global.find((row) => row.isActive) ?? global[0];
}

/** The ratio the backend applies today: the row's value when it is active and valid, otherwise 1. */
function readSavedRatio(row: PlatformSettingListDto | undefined): number {
  if (!row || !row.isActive) return DEFAULT_VM_CPU_LIMIT_RATIO;
  return parseVmCpuLimitRatio(row.value) ?? DEFAULT_VM_CPU_LIMIT_RATIO;
}

/**
 * Edits the VM CPU limit — the share of its cores a VM may use on the host. Customers see CPU usage
 * divided by it, so with 0.7 a VM WiService measures at 70% reads 100%. Stored as the global
 * VmCpuLimitRatio platform setting; saving creates that row the first time.
 */
export function VmCpuLimitCard() {
  const queryClient = useQueryClient();
  const queryKey = ["admin", "platform-settings", "vm-cpu-limit-ratio"];

  const { data: rows, isLoading } = useQuery({
    queryKey,
    queryFn: async () =>
      (
        await adminPlatformSettingService.getSettings({
          type: "VmCpuLimitRatio",
          limit: 50,
        })
      ).data,
  });

  const row = rows ? pickGlobalRow(rows) : undefined;
  const savedRatio = readSavedRatio(row);

  const [input, setInput] = useState("");
  useEffect(() => {
    if (!isLoading) setInput(String(savedRatio));
  }, [isLoading, savedRatio]);

  const parsed = parseVmCpuLimitRatio(input);
  const isDirty = parsed !== null && parsed !== savedRatio;

  const saveMutation = useMutation({
    mutationFn: (value: string) => {
      if (row) {
        return adminPlatformSettingService.updateSetting(row.id, {
          value,
          // An inactive row is ignored by the backend, so saving here also switches it back on.
          isActive: true,
        });
      }

      return adminPlatformSettingService.createSetting({
        type: "VmCpuLimitRatio",
        key: SETTING_KEY,
        value,
        description:
          "Share of its cores a VM may use on the host; customer CPU usage is divided by it.",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "platform-settings"],
      });
      toast.success(
        "VM CPU limit saved. Charts pick it up within about 1 minute.",
      );
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const preview = parsed ?? savedRatio;

  return (
    <Card className="shrink-0 shadow-none">
      <CardContent className="flex flex-col gap-3 p-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Cpu className="h-4 w-4" />
            VM CPU limit ratio
          </div>
          <p className="text-xs text-muted-foreground">
            Share of its cores a VM may use on the host. Customers see CPU usage
            divided by this value: at {preview}, a host reading of{" "}
            {Math.round(preview * 100)}% shows as 100%. Stored readings are not
            changed.
          </p>
          <p className="flex items-center gap-1 text-xs text-warn-ink">
            <Info className="h-3 w-3 shrink-0" />
            Changes take effect within about 1 minute on the client and admin
            charts.
          </p>
        </div>

        <form
          className="flex shrink-0 flex-col gap-1 sm:items-end"
          onSubmit={(event) => {
            event.preventDefault();
            if (parsed !== null) saveMutation.mutate(String(parsed));
          }}
        >
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1">
              <Label htmlFor="vm-cpu-limit-ratio" className="text-xs">
                Ratio (0 – 1)
              </Label>
              <Input
                id="vm-cpu-limit-ratio"
                inputMode="decimal"
                value={input}
                onChange={(event) => setInput(event.target.value)}
                disabled={isLoading}
                aria-invalid={input !== "" && parsed === null}
                className="h-8 w-28"
                placeholder="0.7"
              />
            </div>
            <Button
              type="submit"
              size="sm"
              className="h-8 min-h-8"
              disabled={!isDirty || saveMutation.isPending}
            >
              {saveMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save
            </Button>
          </div>
          {/* Under the input it describes, not across the card. */}
          {input !== "" && parsed === null && (
            <p className="text-xs text-destructive">
              Must be above 0 and at most 1, e.g. 0.7
            </p>
          )}
        </form>
      </CardContent>
    </Card>
  );
}
